import React, { useState } from 'react';
import DesktopOrderRow from './DesktopOrderRow';
import { updateOrderStatus, updateOrderCallStatus } from '../../orders-controller/order-controller'; // Make sure to import the function
import OrderTableFooter from './OrderTableFooter';
import { toast } from 'react-toastify';
import { usePermissionViewModel } from '../../permissions/viewmodel/permission-viewmodel';


const DesktopOrderTable = ({
  orders,
  getAllowedStatusOptions,
  getStatusInfo,
  formatDate,
  getClientFullName,
  handleStatusChange,
  getStatusLabel,
  handleDeleteOrder,
  selectedOrders,
  handleOrderSelection,
  newlyAddedOrders,
  handleTrackOrder,
  isFooterExpanded,
  toggleFooterExpand,
  formatPrice,
  refreshData = () => {},
  handleCallStatusChange: handleCallStatusChangeProp = null
}) => {
  // Add state to track which dropdown is open
  const [openDropdownId, setOpenDropdownId] = useState(null);
  // Add state for confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [statusToApply, setStatusToApply] = useState(null);
  const [statusLabelToApply, setStatusLabelToApply] = useState('');
  // Add state to track loading during bulk operations
  const [isLoading, setIsLoading] = useState(false);
  const {hasPermission} = usePermissionViewModel('commande')

  // Handle call status change
  const handleCallStatusChange = async (orderId, currentCallStatus) => {
    try {
      // Determine new call type based on current status
      let newCallType;
      if (currentCallStatus === null) {
        // If call is null (gray), make it green - send 1
        newCallType = 1;
      } else if (currentCallStatus === true || currentCallStatus === 1) {
        // If call is green (true/1), make it red - send 0
        newCallType = 0;
      } else if (currentCallStatus === false || currentCallStatus === 0) {
        // If call is red (false/0), make it green - send 1
        newCallType = 1;
      }

      await updateOrderCallStatus(orderId, newCallType);
      
      // Refresh the data to show updated status
      refreshData();
      
      // Show success message
      toast.success("Statut d'appel mis à jour avec succès", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error updating call status:', error);
      
      // Show error message
      toast.error("Erreur lors de la mise à jour du statut d'appel", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  
  // Completely rewritten function to handle "select all" checkbox
  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    
    if (isChecked) {
      // When checking, select all orders that aren't already selected
      orders.forEach(order => {
        // Check if this order is already selected
        const isAlreadySelected = selectedOrders.some(selected => selected.id === order.id);
        
        // If not already selected, select it
        if (!isAlreadySelected) {
          // Pass the order ID and status to the handleOrderSelection function
          handleOrderSelection(order.id, order.etat);
        }
      });
    } else {
      // When unchecking, deselect all orders
      selectedOrders.forEach(selectedOrder => {
        handleOrderSelection(selectedOrder.id, selectedOrder.status);
      });
    }
  };
  
  // Function to toggle the header dropdown
  const toggleHeaderDropdown = () => {
    setOpenDropdownId(openDropdownId === 'header' ? null : 'header');
  };

// Update the confirmBulkStatusChange function
const confirmBulkStatusChange = (status, label) => {
  // Only allow status change if there are selected orders
  if (selectedOrders.length === 0) {
    return;
  }
  
  setStatusToApply(status);
  setStatusLabelToApply(label);
  setShowConfirmModal(true);
  setOpenDropdownId(null);
};

// Update the handleBulkStatusChange function to directly update statuses without triggering the StatusUpdateModal
const handleBulkStatusChange = async () => {
  try {
    setIsLoading(true);
    
    // Get IDs of selected orders
    const orderIds = selectedOrders.map(order => order.id);
    
    // Make the API call directly without going through handleStatusChange
    // This way we bypass the second confirmation modal
    const response = await updateOrderStatus(null, statusToApply, orderIds);
    
    // Check for errors
    if (response && response.errorMessage) {
      toast.error(`Erreur: ${response.errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      setIsLoading(false);
      return;
    }
    
    // If no error, show success message
    toast.success(`Statut mis à jour avec succès pour ${selectedOrders.length} commandes`, {
      position: "top-right",
      autoClose: 3000
    });
    
    // Close the modal
    setShowConfirmModal(false);
    
    // Update UI directly without triggering additional modals
    selectedOrders.forEach(selectedOrder => {
      // Update the UI directly (don't call handleStatusChange)
      // Instead, apply the status directly to the order in the UI
      const updatedOrder = {
        ...selectedOrder,
        status: statusToApply
      };
      
      // Update the order in the orders array (this depends on your state management approach)
      // If you're using Redux, dispatch an action to update the order
      // If you're using local state, update the state
      
      // For example, if handleStatusChange is only modifying UI:
      const statusLabel = getStatusLabel(statusToApply);
      directUpdateOrderStatus(selectedOrder.id, statusToApply, statusLabel);
    });
    
  } catch (error) {
    console.error('Error updating order statuses:', error);
    
    toast.error("Une erreur est survenue lors de la mise à jour des statuts", {
      position: "top-right",
      autoClose: 5000
    });
  } finally {
    setIsLoading(false);
  }
};

const directUpdateOrderStatus = (orderId, statusKey, statusLabel) => {
  handleStatusChange(orderId,statusLabel, statusKey, true); // But modify this to not open the modal
}
  
  // Check if all orders are selected
  const allSelected = 
    orders.length > 0 && 
    selectedOrders.length === orders.length && 
    orders.every(order => selectedOrders.some(selected => selected.id === order.id));
  
  // List of all possible statuses (excluding pending)
  const allStatuses = [
    { key: 'confirmed', label: 'Confirmé' },
    { key: 'readyToShip', label: 'Prêt à expédier' },
    { key: 'shipped', label: 'Expédié' },
    { key: 'delivered', label: 'Livré' },
    { key: 'canceled', label: 'Annulé' },
    { key: 'returned', label: 'Retourné' }
  ];
  
  return (
    <div className="hidden lg:block">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center">
                  <input                  
                    type="checkbox"
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded cursor-pointer"
                    checked={allSelected}
                    onChange={handleSelectAll}
                  />
                  <span className="ml-2">Sélection</span>
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID / Référence
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Client
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Appels
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total TTC
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Avis client
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Commentaire
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th scope="col" className="px-6 py-3.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Syncro
              </th>
              <th scope="col" className="relative px-6 py-3">
                <div className="relative">
                  <button
                    onClick={toggleHeaderDropdown}
                    className={`px-3 py-1 text-xs font-medium ${selectedOrders.length > 0 ? 'text-green-600' : 'text-gray-400 cursor-not-allowed'} uppercase tracking-wider focus:outline-none`}
                    title={selectedOrders.length > 0 ? "Changer le statut des commandes sélectionnées" : "Veuillez sélectionner des commandes"}
                    disabled={selectedOrders.length === 0}
                  >
                    Status
                    <svg className="ml-1 h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Dropdown menu with status options - only available when orders are selected */}
                  {openDropdownId === 'header' && selectedOrders.length > 0 && (
                    <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                      <div className="py-1" role="menu" aria-orientation="vertical">
                        <div className="px-4 py-2 text-sm text-gray-700 font-semibold border-b">
                          Changer le statut ({selectedOrders.length} sélectionnées)
                        </div>
                        {allStatuses.map((status) => (
                          <button
                            key={status.key}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                            onClick={() => confirmBulkStatusChange(status.key, status.label)}
                          >
                            {status.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {orders.length === 0 ? (
              <tr>
                <td colSpan="12" className="px-6 py-10 text-center text-sm text-gray-500">
                  Aucune commande ne correspond à vos critères.
                </td>
              </tr>
            ) : (
              orders.map((order) => {
                const allowedOptions = getAllowedStatusOptions(order.etat);
                const statusInfo = getStatusInfo(order.etat);
                const isSelected = selectedOrders?.some(selected => selected.id === order.id) || false;

                return (
                  <DesktopOrderRow
                    key={order.id}
                    order={order}
                    allowedOptions={allowedOptions}
                    statusInfo={statusInfo}
                    isSelectableStatus={true}
                    isSelected={isSelected}
                    handleOrderSelection={handleOrderSelection}
                    formatDate={formatDate}
                    getClientFullName={getClientFullName}
                    handleStatusChange={handleStatusChange}
                    getStatusLabel={getStatusLabel}
                    handleDeleteOrder={handleDeleteOrder}
                    newlyAddedOrders={newlyAddedOrders}
                    handleTrackOrder={handleTrackOrder}
                    handleCallStatusChange={handleCallStatusChangeProp || handleCallStatusChange}
                  />
                );
              })
            )}
          </tbody>
          {/* Add this right after the table body (tbody) section */}
          <tfoot className="bg-gray-50 border-t border-gray-200">
            <tr>
              <td colSpan="9" className="p-0">
                <div className="px-4 py-3 border-b border-gray-200">
                  <button 
                    onClick={toggleFooterExpand}
                    className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    Résumé des commandes ({orders.length})
                    {isFooterExpanded ? (
                      <svg className="ml-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="ml-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </div>
              </td>
            </tr>
            {isFooterExpanded && (
              <>
                <tr className="bg-gray-50 hover:bg-gray-50">
                  <td className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                    Total HT
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                    {formatPrice(orders.reduce((sum, order) => sum + (parseFloat(order.totalHt) || 0), 0))}
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
                <tr className="bg-gray-50 hover:bg-gray-50">
                  <td className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                    Total TVA
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                    {formatPrice(orders.reduce((sum, order) => sum + (parseFloat(order.totalTva) || 0), 0))}
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
                <tr className="bg-gray-50 hover:bg-gray-50">
                  <td className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                    Frais de livraison
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                    {formatPrice(orders.reduce((sum, order) => sum + (
                      parseFloat(order.fraisDeLivraison) || 
                      parseFloat(order.fraisLivraison) || 
                      parseFloat(order.fraisTransport) || 
                      {/* empty placeholder removed */}
                    ), 0))}
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
                <tr className="bg-gray-50 hover:bg-gray-50 font-semibold">
                  <td className="px-6 py-3 text-left text-sm font-medium text-gray-900">
                    TOTAL TTC
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td className="px-6 py-3 text-right text-sm font-bold text-gray-900">
                    {formatPrice(orders.reduce((sum, order) => sum + (parseFloat(order.totalTTC) || 0), 0))}
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </>
            )}
          </tfoot>
        </table>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <svg className="h-6 w-6 text-yellow-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mt-2">Confirmation</h3>
              {/* Update the confirmation modal content */}
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Vous êtes sur le point de changer le statut de <span className="font-bold text-gray-700">{selectedOrders.length}</span> commandes sélectionnées à <span className="font-bold text-gray-700">{statusLabelToApply}</span>.
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Cette action ne modifiera que les commandes que vous avez sélectionnées.
                </p>
              </div>
              <div className="flex justify-center gap-4 mt-3">
                <button 
                  onClick={() => setShowConfirmModal(false)} 
                  disabled={isLoading}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button 
                  onClick={handleBulkStatusChange} 
                  disabled={isLoading}
                  className="px-4 py-2 bg-green-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Mise à jour...
                    </>
                  ) : (
                    'Confirmer'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesktopOrderTable;